import { NgClass, NgIf } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  InputSignal,
  OnInit,
  ViewChild,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  TouchedChangeEvent,
  Validators,
} from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import { isEqual } from 'lodash';
import { DaterangepickerDirective, LocaleService } from 'ngx-daterangepicker-material';
import { distinctUntilChanged, filter, startWith, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { TDateRangeName, DateUtilitiesService as WidgetDateUtilities } from 'scw-digital-boards-widgets';
import { UserEffects } from '../../../state/user/user.effects';
import { DATE_TIME_FORMAT, DateUtilitiesService, rangeNameToDate } from '../../../utilities/date-utilities.service';
import { CustomMatErrorComponent } from '../custom-mat-error/custom-mat-error.component';
import { DateRangePickerPositioningService } from './date-range-picker-positioning.service';
import { IDateRangePickerConfiguration, IDateRangePickerDate } from './date-range-picker.interface';
import { DaterangepickerConfigModule } from './date-range-picker.module';

@Component({
  imports: [
    FormsModule,
    DaterangepickerConfigModule,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    NgClass,
    NgIf,
    CustomMatErrorComponent,
    MatIcon,
  ],
  providers: [
    LocaleService,
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
    },
  ],
  selector: 'app-date-range-picker',
  styleUrls: ['./date-range-picker.component.scss'],
  templateUrl: './date-range-picker.component.html',
})
export class DateRangePickerComponent implements ControlValueAccessor, OnInit {
  public readonly formControl = input.required<FormControl<IDateRangePickerDate | null | undefined>>();
  @Input() public isFormSubmitted = false;
  public configurationInput: InputSignal<IDateRangePickerConfiguration | undefined> = input<
    IDateRangePickerConfiguration | undefined
  >({} as IDateRangePickerConfiguration);
  public readonly ranges = input<TDateRangeName[]>(Object.keys(rangeNameToDate) as TDateRangeName[]);

  @ViewChild(DaterangepickerDirective, { static: false }) pickerDirective!: DaterangepickerDirective;

  public readonly firstDayOfWeekIndex!: number;
  public readonly configuration = computed<IDateRangePickerConfiguration>(() => {
    const label = this.configurationInput()?.label ?? this.defaultConfiguration.label;
    this.formControlStatus();

    return {
      ...this.defaultConfiguration,
      ...this.configurationInput(),
      label:
        this.formControl()?.status !== 'DISABLED' && this.formControl().hasValidator(Validators.required)
          ? `${label}*`
          : label,
    };
  });
  public readonly userDateFormat = computed(() => {
    const dateFormat: string = dayjs()
      .localeData()
      .longDateFormat(`${UserEffects.getUser().dfUserConfiguration.dateFormat}` as 'LL' | 'L');
    const timeFormat = 'LT';

    return this.configuration().isTimePickerShown ? `${dateFormat} ${timeFormat}` : dateFormat;
  });

  public readonly rangesBasedOnConfiguration = computed(() => {
    const ranges = this.configuration().singleDatePicker
      ? this.ranges().filter((rangeName) => ['today', 'yesterday'].includes(rangeName))
      : this.ranges();

    return ranges.reduce(
      (reducePayload, rangeName) => {
        const rangeDates = rangeNameToDate[rangeName]();

        return {
          ...reducePayload,
          [this.translate.instant(`system.label.dateRanges.${rangeName}`)]: [rangeDates.startDate, rangeDates.endDate],
        };
      },
      {} as Record<string, [Dayjs, Dayjs]>,
    );
  });

  public isTouched = false;
  public readonly minDate = computed(() => this.configurationInput()?.minDate ?? dayjs.unix(0));
  public resolvedDrops: 'up' | 'down' = 'down';

  private readonly defaultConfiguration: IDateRangePickerConfiguration = {
    drops: 'down',
    includeArrows: true,
    isTimePickerShown: true,
    opens: 'right',
    showClearButton: false,
    showErrors: true,
    showRanges: false,
    singleDatePicker: false,
    subscriptSizing: 'fixed',
  };

  public readonly formControlStatus = toSignal(
    toObservable(this.formControl).pipe(
      switchMap((formControl) => formControl.statusChanges.pipe(startWith(formControl.status))),
    ),
    { initialValue: 'VALID' },
  );

  public readonly isFormControlTouched = toSignal(
    toObservable(this.formControl).pipe(
      switchMap((formControl) =>
        formControl.events.pipe(
          filter((event): event is TouchedChangeEvent => event instanceof TouchedChangeEvent),
          map((event) => event.touched),
          startWith(formControl.touched),
        ),
      ),
    ),
    { initialValue: false },
  );

  public readonly dateValues = toSignal(
    toObservable(this.formControl).pipe(
      switchMap((formControl) => formControl.valueChanges.pipe(startWith(formControl.value))),
    ),
    { initialValue: null },
  );

  public readonly predefinedEquivalentIfAny = computed(() => {
    const dateValues = this.dateValues();
    const predefinedEquivalentIfAny =
      dateValues?.startDate && dateValues?.endDate
        ? this.widgetDateUtilities.getPredefinedEquivalentIfAny(
            dateValues.startDate.format('YYYY-MM-DD'),
            dateValues.endDate.format('YYYY-MM-DD'),
            Boolean(this.configuration().singleDatePicker),
          )
        : null;

    return predefinedEquivalentIfAny
      ? this.translate.instant(`system.label.dateRanges.${predefinedEquivalentIfAny}`)
      : null;
  });

  onChange: (value: IDateRangePickerDate | undefined | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly translate: TranslateService,
    private readonly destroyRef: DestroyRef,
    private readonly widgetDateUtilities: WidgetDateUtilities,
    private readonly positioningService: DateRangePickerPositioningService,
  ) {
    this.firstDayOfWeekIndex = dayjs.tz().weekday(0).get('d');
    this.destroyRef.onDestroy(() => this.removeRepositionListeners());
  }

  public ngOnInit(): void {
    this.formControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged(isEqual))
      .subscribe((value) => {
        this.onChange(value);
      });
  }

  public onDatesUpdated(range: unknown): void {
    const modifiedRange: IDateRangePickerDate | undefined = range as IDateRangePickerDate;

    if (!modifiedRange?.endDate || !modifiedRange?.startDate) {
      return;
    }

    const newValue = {
      endDate: dayjs.tz((range as IDateRangePickerDate).endDate!.format(DATE_TIME_FORMAT)),
      startDate: dayjs.tz((range as IDateRangePickerDate).startDate!.format(DATE_TIME_FORMAT)),
    };

    if (isEqual(this.formControl().value, newValue)) {
      return;
    }

    this.formControl().setValue(newValue);
  }

  public openDatepicker(clearDates = false): void {
    if (clearDates) {
      this.clearDates(false);
    }

    this.removeRepositionListeners();

    setTimeout(() => {
      this.updateDropsDirection();
      this.pickerDirective.open();
      this.markAsTouched();
      this.handleInDialogPositioning();
    });
  }

  private updateDropsDirection(): void {
    const configuration = this.configuration();

    if (configuration.drops !== 'auto') {
      this.resolvedDrops = configuration.drops ?? 'down';

      return;
    }

    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const datepickerHeight = 350;

    this.resolvedDrops = hostRect.bottom + datepickerHeight > viewportHeight ? 'up' : 'down';
  }

  public clearDates(checkIsTouched = true): void {
    if (!this.configuration().showClearButton || (!this.isTouched && checkIsTouched)) {
      return;
    }

    this.formControl().setValue(null);
  }

  public writeValue(_value: IDateRangePickerDate): void {}

  public markAsTouched(): void {
    this.onTouched();
    this.isTouched = true;
  }

  public registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public adjustDate(amount: 1 | -1): void {
    if (this.formControlStatus() === 'DISABLED') {
      return;
    }

    this.formControl().setValue(DateUtilitiesService.adjustDate(amount, this.dateValues()));
  }

  private handleInDialogPositioning(): void {
    setTimeout(() => {
      const container: HTMLElement | undefined = this.pickerDirective?.picker?.pickerContainer?.nativeElement;

      if (!container) {
        return;
      }

      this.removeRepositionListeners = this.positioningService.apply({
        configuration: this.configuration(),
        container,
        hostElement: this.elementRef.nativeElement,
        isShown: () => Boolean(this.pickerDirective?.picker?.isShown),
        resolvedDrops: this.resolvedDrops,
      });
    });
  }

  private removeRepositionListeners: () => void = (): void => {
    // no-op until the reposition listeners are attached
  };
}
